import { App, Button, Flex, Popconfirm, Typography, Upload } from 'antd';
import { translateApiError } from '@/shared/api/errorToMessage';
import { IMAGE_ACCEPT, validateImageFile } from '@/shared/lib/image-file';
import { ImageThumb } from '@/shared/ui/ImageThumb';
import { useRemoveCategoryImageMutation, useUploadCategoryImageMutation } from '../api/categoryApi';
import type { CategoryDto } from '../model/types';

export interface CategoryImageFieldProps {
  category: Pick<CategoryDto, 'id' | 'name' | 'imageUrl' | 'thumbUrl'>;
}

/** §2.1: фото существующей группы или категории — просмотр, замена, удаление. В отличие от полей
 *  окна, фото сохраняется сразу, без кнопки «Сохранить»: это отдельный запрос с файлом. */
export function CategoryImageField({ category }: CategoryImageFieldProps) {
  const { message } = App.useApp();
  const [uploadImage, { isLoading: uploading }] = useUploadCategoryImageMutation();
  const [removeImage, { isLoading: removing }] = useRemoveCategoryImageMutation();
  const busy = uploading || removing;
  const hasPhoto = !!category.imageUrl;

  function beforeUpload(file: File): false {
    const reason = validateImageFile(file);
    if (reason) {
      message.warning(reason);
      return false;
    }
    uploadImage({ id: category.id, file })
      .unwrap()
      .then(() => message.success('Фото сохранено'))
      .catch((e: unknown) => message.error(translateApiError(e)));
    return false; // отправили сами через RTK Query; antd Upload ничего не шлёт
  }

  async function handleRemove() {
    try {
      await removeImage(category.id).unwrap();
      message.success('Фото удалено');
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <Flex gap={12} align="flex-start">
      <ImageThumb src={category.thumbUrl} previewSrc={category.imageUrl} size={96} alt={category.name} />
      <Flex vertical gap={6} align="flex-start">
        {/* fileList пустой всегда: antd Upload не копит выбранные файлы у себя */}
        <Upload accept={IMAGE_ACCEPT} showUploadList={false} fileList={[]} beforeUpload={beforeUpload} disabled={busy}>
          <Button loading={uploading} disabled={removing}>
            {hasPhoto ? 'Заменить фото' : 'Загрузить фото'}
          </Button>
        </Upload>
        {hasPhoto ? (
          <Popconfirm title="Удалить фото?" okText="Удалить" cancelText="Отмена" onConfirm={() => void handleRemove()}>
            <Button danger loading={removing} disabled={uploading}>
              Удалить фото
            </Button>
          </Popconfirm>
        ) : null}
        <Typography.Text type="secondary">JPG, PNG или WebP, до 10 МБ. Фото сохраняется сразу.</Typography.Text>
      </Flex>
    </Flex>
  );
}
